import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments';
import { AuthTokenService } from '../services/auth-token.service';

const retryHeader = 'X-Auth-Token-Retry';

/**
 * Adds the Cognito access token to REM API requests and retries an expired request once.
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const authToken = inject(AuthTokenService);

  // if (environment.legacyAuthenticationEnabled || !req.url.startsWith(environment.apiUrl)) {
  //   return next(req);
  // }

  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  const token = authToken.getAccessToken();
  if (environment.debugLogging) {
    console.debug('[AuthTokenInterceptor] REM API request', {
      url: req.url,
      hasAccessToken: !!token
    });
  }

  const authenticatedRequest = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authenticatedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || req.headers.has(retryHeader)) {
        return throwError(() => error);
      }

      let refreshRequest$;
      try {
        refreshRequest$ = authToken.refreshAccessToken();
      } catch {
        authToken.handleAuthenticationExpired('missing-refresh-token');
        return throwError(() => error);
      }

      return refreshRequest$.pipe(
        // Only a failed refresh restarts sign-in; a 401 on the retried request is passed on, otherwise it loops.
        catchError(() => {
          authToken.handleAuthenticationExpired('refresh-failed');
          return throwError(() => error);
        }),
        switchMap(() => {
          const refreshedToken = authToken.getAccessToken();
          if (!refreshedToken) {
            authToken.handleAuthenticationExpired('refresh-returned-no-token');
            return throwError(() => error);
          }

          const retryRequest = req.clone({
            setHeaders: {
              Authorization: `Bearer ${refreshedToken}`,
              [retryHeader]: 'true'
            }
          });
          return next(retryRequest);
        })
      );
    })
  );
};